import { useRef, useState } from 'react'

import type { IContext } from './columns'
import type { TModalType } from './ManageAppEventModal'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IAppEvent } from '~/types/event'

import columns from './columns'
import ManageAppEventModal from './ManageAppEventModal'

import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppEventsListQuery, useDeleteAppEventMutation } from '~/queries/events'
import { useUserDetailsQueries } from '~/queries/users'
import { transformByDeviceType } from '~/utils/application'
import { dateSort } from '~/utils/date'
import { displayToast } from '~/utils/toast'

const Events = (): ReactElement => {
  /** Local state */

  const { appDetails, appKey, deviceType } = useAppSummary()

  const eventEditionAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.EVENT.WRITE])

  const gridRef = useRef<AgGridReact<IAppEvent>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isManageEventModalOpen, setIsManageEventModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [selectedEvent, setSelectedEvent] = useState<IAppEvent>()

  const [manageEventModalType, setManageEventModalType] = useState<TModalType>()

  /** Queries & Mutations */

  const { data: events, isLoading: isEventsLoading, refetch } = useAppEventsListQuery({ appKey, deviceType })

  const userIds = events?.filter(({ created_by }) => created_by).map(({ created_by }) => created_by)

  const userQueries = useUserDetailsQueries(userIds)

  const users = userQueries.map(({ data }) => data).filter((el) => !!el)

  const rowData = events
    ?.map((el) => ({ ...el, created_by: users.find((user) => user?.user_id === el.created_by) }))
    .sort((a, b) => dateSort(a.created_at, b.created_at, 'desc'))

  const { mutateAsync: deleteAppEvent } = useDeleteAppEventMutation()

  /** Effects */

  useDocumentTitle('Events')

  /** Handlers */

  const handleManageEventModalState = (value: boolean) => () => setIsManageEventModalOpen(value)

  const handleDeleteModalState = (value: boolean) => () => setIsDeleteModalOpen(value)

  const handleCreateEvent = () => {
    setManageEventModalType('create')
    setIsManageEventModalOpen(true)
  }

  const handleEditEvent = (data: IAppEvent) => {
    setManageEventModalType('edit')
    setIsManageEventModalOpen(true)
    setSelectedEvent(data)
  }

  const handleManageEventFailure = () => {
    setIsManageEventModalOpen(false)
    displayToast(`Event could not be ${manageEventModalType === 'create' ? 'created' : 'updated'}`, 'error')
  }

  const handleManageEventSuccess = () => {
    setIsManageEventModalOpen(false)
    refetch()
    displayToast(`Event successfully ${manageEventModalType === 'create' ? 'created' : 'updated'}`, 'success')
  }

  const handleDeleteEvent = (data: IAppEvent) => {
    setIsDeleteModalOpen(true)
    setSelectedEvent(data)
  }

  const deleteRow = async () => {
    if (selectedEvent) {
      try {
        await deleteAppEvent({ appKey, deviceType, eventId: selectedEvent.id })
        refetch()
        gridRef.current?.api?.refreshCells({ force: true })
        displayToast('Event successfully deleted', 'success')
      } catch {
        displayToast('Event could not be deleted', 'error')
      }
    }

    setIsDeleteModalOpen(false)
  }

  const context: IContext = {
    handleDeleteEvent,
    handleEditEvent,
  }

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="events-view" withPadding>
        <ViewTitle className="layout-view-title">App events</ViewTitle>
        <ContentLayout loading={isEventsLoading}>
          <TableWrapper
            addButton
            addButtonDisabled={!eventEditionAuthorized}
            addButtonLabel="New event"
            addButtonTooltip={!eventEditionAuthorized ? 'unauthorized' : undefined}
            className="events-table with-min-height"
            columnDefs={columns}
            context={context}
            defaultColDef={{
              menuTabs: [],
            }}
            entriesName="events"
            onAddButtonClick={handleCreateEvent}
            ref={gridRef}
            rowData={rowData}
            rowStyle={{ cursor: 'default' }}
            suppressContextMenu
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      {eventEditionAuthorized ? (
        <>
          {appKey && deviceType && manageEventModalType ? (
            <ManageAppEventModal
              appKey={appKey}
              deviceType={deviceType}
              event={selectedEvent}
              isOpen={isManageEventModalOpen}
              modalType={manageEventModalType}
              onClose={handleManageEventModalState(false)}
              onFailure={handleManageEventFailure}
              onSuccess={handleManageEventSuccess}
            />
          ) : null}
          <ModalDialog
            isOpen={isDeleteModalOpen}
            mainText="Do you really want to delete this event?"
            onClose={handleDeleteModalState(false)}
            onConfirm={deleteRow}
          />
        </>
      ) : null}
    </>
  )
}
export default Events
